<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@okta/okta-vue';
import authService from '@/services/AuthService';
import { ensureCapabilitiesLoaded } from '@/composables/useCapabilities';
import { ensureSelectedBrandLoaded } from '@/composables/useSelectedBrand';
import AuthConsoleCard from '@/components/auth/AuthConsoleCard.vue';
import AuthConsoleStatus from '@/components/auth/AuthConsoleStatus.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';

const router = useRouter();
const $auth = useAuth();

const loading = ref(true);
const errorMessage = ref('');

onMounted(async () => {
    try {
        await $auth.handleLoginRedirect();

        // Sync Okta SDK tokens into Vuex and start listening for renewals
        authService.syncTokensFromOkta();
        authService.startTokenSync();

        // Force-refresh RBAC capabilities for this admin, since a previous admin's
        // set may be persisted in localStorage (issue #1878).
        await ensureCapabilitiesLoaded(true);

        // Force-refresh the selected brand from the backend for the same reason:
        // a previous admin's brand may be persisted in localStorage (#2122).
        await ensureSelectedBrandLoaded(true);

        // Log straight into the dashboard, scoped to the admin's last-used brand
        // (persisted on admin.user.selected_brand_code; defaults to giddyup for a
        // brand-new admin). Brand selection is NOT forced after login: the admin
        // switches brands on demand from the profile menu (SelectBrandView). This
        // keeps sign-in friction-free while the brand list stays backend-driven
        // and never hardcoded (#2230, #2122).
        console.log('Authentication successful, redirecting to dashboard');
        router.push({ name: 'dashboard' });
    } catch (error) {
        console.error('OAuth callback error:', error);
        errorMessage.value = error.message || 'Authentication failed. Please try again.';
        loading.value = false;
    }
});
</script>

<template>
    <AuthConsoleCard>
        <AuthConsoleStatus :label="loading ? 'Signing you in' : 'Authentication failed'" :busy="loading" />

        <template v-if="errorMessage">
            <KHKNotificationCard :notification="{ message: errorMessage }" type="error" class="w-full mt-4" />
            <div class="con-field">
                <router-link :to="{ name: 'login' }" class="con-return">‹ return to login</router-link>
            </div>
        </template>

        <div v-else class="con-foot">© {{ new Date().getFullYear() }} KHK Games. All rights reserved.</div>
    </AuthConsoleCard>
</template>

<style scoped>
.con-return {
    font-family: var(--con-mono);
    font-size: 13px;
    color: var(--con-primary);
}

.con-return:hover {
    text-decoration: underline;
}
</style>
